import { Col, Row } from 'antd';
import { Pin } from 'types';
import { formatToDate } from 'utils/formatToDate';

import {
  CERTIFICATE_HEALTH_DESCRIPTION,
  DocumentDetailsModel,
} from 'api/documents/documents.model';
import { BasicCard } from 'components/BasicCard';
import CardRow from 'components/CardRow';
import CompositionRow from 'components/CompositionRow';
import Detail from 'components/Detail';
import Map from 'components/Map';
import { extractCoordinatesFromProduct } from 'components/Map/utils';
import SectionTitle from 'components/SectionTitle';
import SubTitle from 'components/SubTitle';
import useCertificateHelper from 'hooks/useCertificateHelper';
import {
  ADDITIONAL_DETAILS,
  CertificateDetailsModel,
  NESTED_DETAILS,
  PRODUCER_DETAILS,
} from 'models/certificate.model';

import HealthDescription from '../components/HealthDescription';
import NotProvidedValueCard from '../components/NotProvidedValueCard';
import RelatedCertificates from '../components/RelatedCertificates';
import Template from '../components/Template';

const BatteryPackRepair = ({
  additionalDetails = {} as CertificateDetailsModel,
  ...document
}: DocumentDetailsModel<CertificateDetailsModel>) => {
  const { getLabel, getValueByLabel } = useCertificateHelper();

  const description = getValueByLabel(
    NESTED_DETAILS.DESCRIPTION,
    additionalDetails[ADDITIONAL_DETAILS.DETAILS]
  );

  const pins: Pin[] = [];

  if (additionalDetails[ADDITIONAL_DETAILS.PRODUCER]) {
    const coordinates = extractCoordinatesFromProduct(
      additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
    );

    if (coordinates) {
      pins.push({
        coordinates,
        iconPath: '/pin-blue.svg',
      });
    }
  }

  return (
    <Template
      type={document.category}
      jobId={document.jobId}
      favourite={document.favourite}
      batchCard={
        <>
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail label={getLabel('passportId')} value={document.documentBlockchainAddress} />
          <CardRow>
            <Detail
              label={getLabel(ADDITIONAL_DETAILS.ASSEMBLY_NUMBER)}
              value={additionalDetails[ADDITIONAL_DETAILS.ASSEMBLY_NUMBER]}
            />
            <Detail
              label={getLabel(ADDITIONAL_DETAILS.CONTROL_NUMBER)}
              value={additionalDetails[ADDITIONAL_DETAILS.CONTROL_NUMBER]}
            />
          </CardRow>
        </>
      }
      detailsCard={
        <CardRow>
          <Detail
            label={getLabel(NESTED_DETAILS.EVMODEL)}
            value={getValueByLabel(
              NESTED_DETAILS.EVMODEL,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PRODUCERS)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_PRODUCERS,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_CELL_MODEL)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_CELL_MODEL,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PRODUCTION_DATE)}
            value={formatToDate(
              getValueByLabel(
                NESTED_DETAILS.BATTERY_PRODUCTION_DATE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PACKS_GLOBAL_TRADE_IDENTIFICATION_NUMBER)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_PACKS_GLOBAL_TRADE_IDENTIFICATION_NUMBER,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel('repairCompanyFullName')}
            value={getValueByLabel(
              PRODUCER_DETAILS.COMPANY,
              additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
            )}
          />
          <Detail
            label={getLabel('baterryPackTechnicalReviewCertificateUID')}
            value={additionalDetails.productId}
          />
        </CardRow>
      }
    >
      <Row gutter={[16, 16]}>
        <Col md={24} lg={10}>
          <SectionTitle>{getLabel(NESTED_DETAILS.REPAIR_DESCRIPTION)}</SectionTitle>

          <SubTitle>{getLabel('repairInfo')}</SubTitle>
          <BasicCard>
            <CardRow>
              <Detail
                label={getLabel(NESTED_DETAILS.REPAIR_DESCRIPTION)}
                value={getValueByLabel(
                  NESTED_DETAILS.REPAIR_DESCRIPTION,
                  additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                )}
              />
              <Detail
                label={getLabel(NESTED_DETAILS.REPAIR_DATE)}
                value={formatToDate(
                  getValueByLabel(
                    NESTED_DETAILS.REPAIR_DATE,
                    additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                  )
                )}
              />
            </CardRow>
          </BasicCard>
          <br />
          <SubTitle>{getLabel(NESTED_DETAILS.HEALTH_DESCRIPTION)}</SubTitle>
          <HealthDescription
            value={
              getValueByLabel(
                NESTED_DETAILS.HEALTH_DESCRIPTION,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              ) as CERTIFICATE_HEALTH_DESCRIPTION
            }
          />
        </Col>
        <Col md={24} lg={14}>
          <RelatedCertificates
            additionalDetails={additionalDetails}
            relatedFields={['batteryPackSerialNumber']}
          />
        </Col>
      </Row>

      <SectionTitle>{getLabel('specification')}</SectionTitle>
      <SubTitle>{getLabel('generalInfo')}</SubTitle>
      <CompositionRow<NESTED_DETAILS>
        composition={additionalDetails[ADDITIONAL_DETAILS.DETAILS]}
        labels={[
          NESTED_DETAILS.CELLS_SETUP,
          NESTED_DETAILS.TOTAL_CAPACITY,
          NESTED_DETAILS.USABLE_CAPACITY,
          NESTED_DETAILS.WEIGHT,
          NESTED_DETAILS.GRAVIMETRIC_ENERGY_DENSITY_CLEAN,
          NESTED_DETAILS.COOLING,
          NESTED_DETAILS.STORAGE_TEMP_RANGE,
        ]}
      />

      <SubTitle>{getLabel('description')}</SubTitle>
      {description ? <BasicCard>{description}</BasicCard> : <NotProvidedValueCard />}

      <SectionTitle>{getLabel('repairCompany')}</SectionTitle>
      <Map
        pins={pins}
        card={
          <BasicCard>
            <SectionTitle noMargin dividerMargin="1rem">
              {getLabel(PRODUCER_DETAILS.COMPANY)}
            </SectionTitle>
            <Detail
              label={getLabel('repairCompanyFullName')}
              value={getValueByLabel(
                PRODUCER_DETAILS.COMPANY,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <Detail
              label={getLabel('repairLocation')}
              value={getValueByLabel(
                PRODUCER_DETAILS.FACTORY_LOCATION,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
          </BasicCard>
        }
      />
    </Template>
  );
};

export default BatteryPackRepair;
